/** @format */

/**
 * 会话链路 RootSpans 的时间轴视图：
 * 顶部为按「最长 Span 耗时」归一化的秒刻度；每行左侧为调用序号与开始时间，右侧条形长度为耗时占比。
 * Hover 条形可通过鼠标跟随浮层查看 Span 概要。
 */

import React, { useMemo } from 'react'
import styled from 'styled-components'
import { DescriptionItem, Descriptions, _$, dayjs } from '@king-design/react'
import MouseFollowHint from '../../components/MouseFollowHint'
import type { SessionDetailRootSpan } from './typescript'
import { FormatCompactK } from 'src/components'
import { secondsFormat } from 'src/utils/common'
import variable from 'src/static/styles/var'

/** 横向刻度分段数（含起点 0 与终点，共 SEGMENTS+1 个刻度点） */
const AXIS_SEGMENTS = 8

/** 时间轴刻度展示：`0` 或无耗时时不带 `s` 后缀 */
function formatAxisTick(seconds: number): string {
  if (seconds <= 0) {
    return '0'
  }
  return `${seconds.toFixed(2)}s`
}

/** 左侧标签内的调用开始时间 */
function formatRowTime(iso?: string): string {
  return iso ? dayjs(iso).format('YYYY-MM-DD HH:mm:ss') : '-'
}

interface RootSpansTimelineProps {
  className?: string
  spans: SessionDetailRootSpan[]
  toTraceDetail: (trace: { TraceId: string; StartTime: string }) => void
  /** 当前高亮的 TraceId（与时间轴选中态同步） */
  activeTraceId?: string
  /** 点击时间轴条目，锚点滚动到对应 SpanItem */
  onTraceClick?: (traceId: string) => void
}

const RootSpansTimeline: React.FC<RootSpansTimelineProps> = ({
  className,
  spans,
  toTraceDetail,
  activeTraceId,
  onTraceClick
}) => {
  /** 按开始时间先后排序，保证「第 N 次调用」与时间顺序一致 */
  const sorted = useMemo(
    () => [...spans].sort((a, b) => dayjs(a.StartTime).valueOf() - dayjs(b.StartTime).valueOf()),
    [spans]
  )

  /** 所有行共用的横向比例尺上限；至少为 1ms，避免除零 */
  const maxDurationMs = useMemo(() => {
    const max = Math.max(...sorted.map(s => s.DurationMs), 0)
    return max > 0 ? max : 1
  }, [sorted])

  const maxSeconds = maxDurationMs / 1000

  /** 在 [0, maxSeconds] 上均分刻度，用于与条形宽度同一套比例 */
  const ticks = useMemo(() => {
    const list: number[] = []
    for (let i = 0; i <= AXIS_SEGMENTS; i++) {
      list.push((maxSeconds * i) / AXIS_SEGMENTS)
    }
    return list
  }, [maxSeconds])

  if (!sorted.length) {
    return <div className={`${className} root-span-timeline-empty`}>{_$('暂无链路数据')}</div>
  }

  /** 浮层内容：与本页网关字段对齐，按需扩展 DescriptionItem */
  const renderSpanHint = (span: SessionDetailRootSpan) => {
    const hasSpanToken = span.TotalTokens > 0 || span.InputTokens > 0 || span.OutputTokens > 0
    return (
      <div style={{ width: '420px', paddingRight: '8px' }}>
        <Descriptions columns={1}>
          <DescriptionItem label={_$('TraceID')} style={{ alignItems: 'flex-start' }}>
            <a
              className="link-hover-underline"
              onClick={() => {
                toTraceDetail({
                  TraceId: span.TraceId,
                  StartTime: span.StartTime
                })
              }}
            >
              {span.TraceId}
            </a>
          </DescriptionItem>
          <DescriptionItem label={_$('SpanID')} style={{ alignItems: 'flex-start' }}>
            {span.SpanId}
          </DescriptionItem>
          <DescriptionItem label={_$('名称')} style={{ alignItems: 'flex-start' }}>
            {span.Name}
          </DescriptionItem>
          <DescriptionItem label={_$('耗时')}>{secondsFormat(span.DurationMs)}</DescriptionItem>
          {hasSpanToken ? (
            <DescriptionItem label={_$('Token')}>
              <FormatCompactK value={span.TotalTokens} />
            </DescriptionItem>
          ) : null}
        </Descriptions>
      </div>
    )
  }

  return (
    <div className={className}>
      {/* 与下方条形区域右对齐：左侧留白宽度与 meta 列一致 */}
      <div className="root-span-timeline-axis-row">
        <div className="root-span-timeline-meta-spacer" aria-hidden />
        <div className="root-span-timeline-axis">
          {ticks.map((t, i) => (
            <span
              key={i}
              className="root-span-timeline-tick"
              style={{ left: `${(i / AXIS_SEGMENTS) * 100}%` }}
            >
              {formatAxisTick(t)}
            </span>
          ))}
        </div>
      </div>
      <div className="root-span-timeline-rows">
        {sorted.map((span, index) => {
          /** 条形宽度 = 本 Span 耗时 / 全局最长耗时 */
          const pct = Math.min(100, Math.max(0, (span.DurationMs / maxDurationMs) * 100))
          const isSuccessDuration = span.DurationMs <= 10 * 1000
          const isWarningDuration = span.DurationMs > 10 * 1000 && span.DurationMs <= 30 * 1000
          const barClass = [
            'root-span-timeline-bar',
            isSuccessDuration ? 'root-span-timeline-bar--success' : '',
            isWarningDuration ? 'root-span-timeline-bar--warning' : '',
            !isSuccessDuration && !isWarningDuration ? 'root-span-timeline-bar--danger' : ''
          ]
            .filter(Boolean)
            .join(' ')
          const isActive = activeTraceId === span.TraceId
          return (
            <div
              key={`${span.TraceId}-${span.SpanId}-${index}`}
              className={[
                'root-span-timeline-row',
                isActive ? 'root-span-timeline-row--active' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              title={span.Name}
            >
              <div className="root-span-timeline-meta">
                <span className="root-span-timeline-call">
                  {_$(`第{x}次调用`, { x: index + 1 })}
                </span>
                <span className="root-span-timeline-time-tag">{formatRowTime(span.StartTime)}</span>
              </div>
              <div className="root-span-timeline-track">
                <MouseFollowHint content={renderSpanHint(span)} hoverable>
                  <div
                    className="root-span-timeline-bar-trigger"
                    style={{ width: `${pct}%` }}
                    role="button"
                    tabIndex={0}
                    onClick={() => onTraceClick?.(span.TraceId)}
                  >
                    <div className={barClass} />
                  </div>
                </MouseFollowHint>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default styled(RootSpansTimeline)`
  box-sizing: border-box;
  padding: 16px 0;

  /* 顶部时间刻度 */
  .root-span-timeline-axis-row {
    display: flex;
    align-items: flex-end;
    margin-bottom: 8px;
    min-height: 28px;

    .root-span-timeline-meta-spacer {
      flex: 0 0 min(36%, 240px);
      max-width: 280px;
      flex-shrink: 0;
    }

    .root-span-timeline-axis {
      position: relative;
      flex: 1;
      min-width: 0;
      height: 28px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.08);

      .root-span-timeline-tick {
        position: absolute;
        bottom: 4px;
        transform: translateX(-50%);
        font-size: 12px;
        line-height: 1;
        color: rgba(0, 0, 0, 0.45);
        white-space: nowrap;

        &:first-child {
          transform: translateX(0);
        }

        &:last-child {
          transform: translateX(-100%);
        }
      }
    }
  }

  /* 每行：左侧 meta + 右侧耗时条 */
  .root-span-timeline-rows {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .root-span-timeline-row {
      display: flex;
      align-items: center;
      min-height: 28px;

      &.root-span-timeline-row--active {
        .root-span-timeline-time-tag {
          color: #0091ea;
          background: rgba(0, 145, 234, 0.08);
        }
      }

      .root-span-timeline-meta {
        flex: 0 0 min(36%, 240px);
        max-width: 280px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
        font-size: 12px;
        color: rgba(0, 0, 0, 0.85);

        .root-span-timeline-call {
          flex-shrink: 0;
          white-space: nowrap;
        }

        .root-span-timeline-time-tag {
          flex-shrink: 1;
          min-width: 0;
          padding: 2px 8px;
          border-radius: 4px;
          background: rgba(0, 0, 0, 0.04);
          color: rgba(0, 0, 0, 0.65);
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          line-height: 22px;
        }
      }

      .root-span-timeline-track {
        flex: 1;
        min-width: 0;
        height: 14px;
        background: rgba(0, 0, 0, 0.03);
        border-radius: 2px;
        display: flex;
        align-items: stretch;

        .root-span-timeline-bar-trigger {
          align-self: stretch;
          min-width: 2px;
          display: flex;
          cursor: pointer;

          .root-span-timeline-bar {
            flex: 1;
            width: 100%;
            min-width: 2px;
            border-radius: 2px;
            background: #52c41a;
            transition: width 0.2s ease;

            &.root-span-timeline-bar--success {
              background: ${variable.successColor};
            }

            &.root-span-timeline-bar--warning {
              background: ${variable.warningColor};
            }

            &.root-span-timeline-bar--danger {
              background: ${variable.errorColor};
            }
          }
        }
      }
    }
  }

  &.root-span-timeline-empty {
    padding: 48px;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    /* background: #fafafa; */
  }
`
